// Every number the opponent argues with, in one place.
//
// These were module constants scattered across `opponent.ts`, `factory.ts` and
// `tasks.ts` — each one reasoned about carefully where it sat, and each one
// reachable only by editing the file it was in. That is fine while a person is
// tuning one term at a time against a watched game, and useless the moment you
// want to *search* the space: a genetic tuner needs a vector it can mutate, a
// runner that will play with it, and a score at the end.
//
// So this is the vector. Three rules keep it honest:
//
// - **The defaults are exactly what the constants were.** Extracting them must
//   not change a single game, and `tuning.test.ts` pins that by playing the
//   same board with `WEIGHTS` and with nothing and comparing state hashes. A
//   refactor that quietly retunes is a refactor whose A/B is meaningless.
// - **It is plain data.** No functions, no classes, no `Map` — it crosses the
//   worker boundary in a message and it gets written to and read from JSON by
//   whatever is doing the searching. Anything clever here is a thing the tuner
//   cannot serialise.
// - **It is not `RULES`.** `src/engine/rules/rules.ts` holds the *game's* tunables —
//   what a recipe costs, how big a buffer is — and changing one changes what
//   the game *is*, for every player, including in a networked game. These are
//   about how well one opponent plays, they live in `src/engine/ai/`, and no rule may
//   ever read them. Two different questions that would be one file only
//   because both contain numbers.
//
// `Skill` (difficulty.ts) stays separate and above this: it is *which* player
// this is — how often they blunder, how far they look — and a tuner searching
// weights should not be able to accidentally make the beginner's opponent
// strong. `Tuning` (also difficulty.ts) is the two knobs a person gets on the
// scenario screen; this is the several dozen a machine gets.

// The rules' own flow map, mirrored as this vector's defaults. The one place
// this file reads `RULES` — and it reads only the *defaults*, so the rule about
// weights never being rules still holds: what a side actually routes by is
// `Position.demand`, which these are copied into at the opening.
import { DEMAND, DEMAND_DEFAULT } from "src/engine/rules/rules";
import {
  PARAMS as PERCEPTRON_PARAMS,
  resizeParams,
  echoFor,
  repairHead,
  reviveHead,
  defaultParams,
  SHAPE,
  OUTPUT_SCALE
} from "./perceptron";

/**
 * Bumped by hand whenever the *planner* changes what a weight means.
 *
 * A champion is a vector tuned against a particular opponent, and the opponent
 * is code. Adding a term, moving one between branches, or changing what
 * `evaluate` returns all make a stored champion a set of answers to a question
 * nobody is asking any more — and unlike a changed goal or a changed batch,
 * nothing about it is visible in the data. The tuner already refuses a
 * champion whose goal or batch moved; this is the third thing that has to
 * match, and it is the only one that cannot be derived.
 *
 * Bump it when scoring semantics move. Adding a field with a neutral default
 * does not count: an old champion simply inherits the default, which is what
 * `withWeights` is for.
 *
 * v3: `deadTrack` and `strandedFlow`. Both are new fields an old champion
 * inherits by default, so no `MIGRATIONS` entry is needed — but neither default
 * is *neutral*. `evaluate` now charges for track that earns nothing and
 * `inFlightValue` discounts material parked on it, so every stored vector is
 * being judged by a board it was not searched against. That is precisely what
 * this number is for.
 *
 * v4: **the planner learned that a lane moves the piece standing on it.** No
 * field was added or removed and `evaluate` is untouched, which is exactly the
 * case this number exists for — nothing about it is visible in the data.
 * `scoreMove` now measures every distance term, and runs its whole reply
 * search, against the square the tick will leave the mover on rather than the
 * square it moved to, so `aggression`, `kingPull`, `flowUrgency` and `greed`
 * are all being paid on a different quantity; `wantedLanes` and
 * `wantedRotations` refuse to build a ride that walks a piece backwards; and a
 * flow whose lane is already delivering is dropped. Every stored champion was
 * selected against a planner that scored the wrong square.
 *
 * v5: **a part costs iron, and an ally's lane is not crushed.** `scoreMove`'s
 * `crushBelt` now asks `areEnemies` rather than "not mine", so it stops paying
 * for a demolition that no longer happens; and `laneBetween` builds fewer
 * routes, because a lane under a piece is refused by the rules rather than
 * merely disliked. No field moved, which is again the case this number is for.
 *
 * v6: **the board learned what a drawn part costs.** `beltAffordability` and
 * `beltFundRate` are new fields an old champion inherits by default, and
 * neither default is *neutral* — `evaluate` now charges for a run a side
 * cannot pay for and credits one it is actually paying for, so every stored
 * vector is being judged against a board it was not searched on. `wantedLanes`
 * also stops drawing past `beltDebt`, which changes what a turn contains.
 *
 * v7: **`scoreMove` returns `winValue` for taking a king.** A new early return
 * rather than a term, and the sharpest possible change to what the function
 * answers — `PIECE_VALUE[KING]` is zero, so the one capture that ends a game
 * outright used to rank level with a quiet move. Reachable only where a belt
 * has carried two kings together, which is also a rules change (`METRICS_VERSION`
 * 7); both moved, and they moved for the same reason.
 *
 * v8: **the flow map became an army trait, and a move on your own lane has a
 * price.** Two changes and both are the case this number exists for. The eight
 * `demand*` weights are copied onto the board at the opening (`demandRow`,
 * `setDemand`), so a champion is now searched in an economy whose *plumbing*
 * it chose — where every stored vector was selected under one shared flow map
 * it never had a say in. And `ownLaneStep` is a new term in `scoreMove` whose
 * default is deliberately **not** neutral: boarding your own lane was free and
 * is now worth something, so every quiet move onto track scores differently
 * from the board every champion was chosen against.
 *
 * v9 -> 10: **a pawn is paid to walk at somewhere it can promote.**
 * `promotionChase` is a new term in `scoreMove`, so every quiet pawn move
 * scores differently from the board every champion was chosen on — and it
 * lands beside a *rules* change (`METRICS_VERSION` 10) that gives pawns a wall
 * to march at on any square, whole board bigger than the classic one, which is
 * half of what the tuner now plays.
 *
 * v9: **two changes to what a foundry saves for, and one weight retired.**
 * `mintCeiling` now counts metal already in hand as well as ovens, so a side
 * holding high-grade ore can name the recipes that ore pays for — which makes
 * rook and queen reachable on boards where every stored champion was searched
 * against a chain that could only ever name a pawn. `forgePatience` is a new
 * field an old champion inherits by default, and it gates how long a recipe is
 * held before it is abandoned. And `kingBreathing` is **gone**: the guard that
 * refused a mint filling the king's last square is retired, so a king now
 * blocks with minted pieces in a caging scenario rather than declining to.
 * A stored vector carrying `kingBreathing` has it dropped and named.
 *
 * v11: **Coronation, and three weights for the army built on it.** `evaluate`
 * now scores a royal beyond the first (`regents`), which nothing could see
 * before — the piece sweep prices a king at `w.piece[KING]`, which is zero, so
 * an army that crowned a queen scored exactly what it scored the turn before.
 * `scoreMove` no longer returns `winValue` for taking *any* king, only for a
 * side's last one, because a spare is progress and not a win. And the planner
 * offers a new kind of turn: `upgradeCandidates`, priced by `upgradeAppetite`
 * and `regentValue`. Every stored champion is re-measured — the three new
 * fields default to the table and an old vector inherits them.
 *
 * v12: **the nonlinear head is live by default and re-flashed when it is
 * dead.** It used to default to zeros, which is the degenerate case exactly —
 * a flat output for every board, nothing to select on, no slope to climb — and
 * four of eight promoted heads were still inert because of it. `seedParams` is
 * fan-in scaled with zero biases, `isFlatHead` is the test for a head that
 * answers the same whatever the board is, and `reviveHead` replaces one
 * wherever a vector is read. Every stored champion is re-measured: the
 * evaluation is no longer a pure weighted sum on any board.
 *
 * v13: **the head gets a seventh input — the turn count.** The first slot that
 * is not a subtotal, and the point of it is that it makes the other six
 * *conditional*: "an unbuilt factory is fine on turn five and a disaster on
 * turn eighty" is an interaction, which is the one thing a coefficient cannot
 * say. Centred and scaled on its own (`TURN_MID`, `TURN_SCALE`) rather than by
 * `INPUT_SCALE`, or a whole game would span the flat middle of `tanh`. A
 * stored 32-parameter head is grown into the new shape by `MIGRATIONS`.
 *
 * v14: **the head sees the score it is correcting, and layer one gains five
 * spare slots.** `linear` is the estimate so far fed back — as near recurrence
 * as this can honestly get, since `evaluate` is a pure function of a board and
 * has no previous position to offer. The spares are always zero and so
 * contribute nothing; what they buy is that the *next* input is a line in
 * `INPUTS` rather than a reshape, a migration and a re-measured field.
 *
 * v15: **the head is told what it is being asked for, and four of the five
 * spares are filled.** `mode` says evaluating or scoring, and it is what makes
 * the other three possible: `choke`, `idle` and `debt` are read off the
 * `FactoryView` that `evaluate` already builds, and left at nought in
 * `scoreMove`, where building one per candidate is the cost that call site
 * exists to avoid. Without `mode` those zeros would be ambiguous — a smooth
 * factory and a question never asked look identical — and with it they are a
 * condition the head can learn. No reshape: the slots were already there.
 *
 * v16: **a fourth kind of turn.** `sacrificeCandidates` puts taking one of your
 * own pieces apart beside forging, raising and climbing, priced by
 * `sacrificeAppetite` — so the ordering has a candidate in it that no stored
 * champion was ever measured against. The rules moved with it (a sacrifice
 * costs the turn now, which is what makes it weighable against a move at all),
 * so `METRICS_VERSION` went too.
 *
 * v17: **the head answers a second question — how much track to lay.** Belt
 * work was the one decision in the turn nothing searched: laying is free of
 * tempo, so `evaluate` is never asked whether a run was
 * worth drawing, and the whole appetite was one hand-set number (`beltDebt`)
 * behind a gate. Now that a part costs iron that a recipe wanted, that is a
 * decision with a real price and it belongs in the search.
 *
 * It is a *second output* rather than another weight because the answer is an
 * interaction — pave when the factory is choked and the ore is spare, stop
 * when the metal is wanted elsewhere — and a coefficient cannot express a
 * shape. The head already reads `debt` as its twelfth input; it simply had no
 * way to answer.
 *
 * A migrated champion is unmoved: the new parameters pad with zero and a zero
 * output exponentiates to a multiplier of one. The number moves anyway,
 * because the planner now asks a question it did not ask before.
 *
 * v19: **how loudly the head may speak is an army's own weight.** The scale on
 * its score answer was a constant every style shared, so an army whose
 * decisions are mostly interactions and one whose whole claim is a straight
 * material count leaned on the network exactly as hard — the one thing about
 * the head that is obviously a style, and the one thing that was not
 * searchable. `w.head` is declared per army and searched like anything else.
 *
 * The default is the constant it replaced, so a champion nobody has
 * re-measured plays what it always played — but the declared values are
 * departures and take effect at once, because no champion has a ratio for a
 * weight that did not exist and `styleWeights` leaves the style's claim
 * standing when it has none.
 *
 * v20: **the head was never searchable, and the test for that was broken.**
 * Three faults compounded, each hiding the next:
 *
 * - `jitter` kicks a weight sitting at zero to about ±3.5, which is right for a
 *   coefficient in a table running to thousands and twelve times too big for a
 *   network parameter whose live value is `1/√13`. Measured: **one `mutate`
 *   pass over a fresh seed head took its response from 16 centipawns to a
 *   median of nought**, with 56% of boards pinned at the output rail. The first
 *   time the search touched a head, it killed it.
 * - `isFlatHead` rolled `mode` across ±1200 when the game only ever passes −1,
 *   1, 2 or 3, so that one input drowned the other twelve and the probe read a
 *   saturated switch as a responsive network. Two of the promoted field's
 *   heads were dead and certified live: ravager 33.2 on the old sampling and
 *   **1.8** with the flag held to a real value, classic 58.6 and **3.5**.
 * - `revive` then re-rolled a condemned head uniformly across ±3 regardless of
 *   fan-in — a draw that pins 56% of boards, so the rescue landed in the same
 *   saturated regime it was rescuing from.
 *
 * What moves for the *game* is `withWeights`: `isFlatHead` now judges all three
 * outputs on realistically sampled boards, `reviveHead` rescales a saturated
 * head before replacing it, and a replacement is salted so two armies arriving
 * dead do not share one evaluation. Champions carry forward and are
 * re-measured, as at every other bump.
 *
 * `METRICS_VERSION` does **not** move with it: no rule changed, the economy is
 * the one every stored score was taken in, and what a metric means is
 * untouched.
 *
 * v21 and v22 are the two reshapes, and `MIGRATIONS` is their record: the
 * waist widened `13x3x2x3 -> 13x8x5x3`, then the input layer took four
 * distance slots, `13x8x5x3 -> 17x8x5x3`.
 *
 * v23: **the head's `choke` input was a constant, and for one army it was a
 * constant of the wrong sign.** `evaluate` filled it as `1 - throughput /
 * capacity`, on the reading that `capacity` is how wide a plant is. It is not:
 * `capacityValue` scores the stages a side has **no** machine for, so
 * `min(machines, 1)` takes every term to nought the moment somebody owns a
 * pawn, a king and a rook. Measured over six hundred boards across eleven
 * armies: `capacity > 0` on 0% of them for nine of the (since retired)
 * profiles, 2% for the factory one (only where a stage had been wiped out),
 * and **100% for the army with no factory at all**, where the input sat at a flat
 * one. The slot that says "my plant is choked" fired only for armies with no
 * plant.
 *
 * It reads `FactoryView.widest` now — the widest stage against `throughput`'s
 * narrowest, both out of one pass and both scaled alike, so the ratio is a
 * share. Every champion in the field had that input column searched against a
 * constant, which is why this is a bump rather than a fix: one of thirteen
 * inputs starts carrying signal, and eight layer-one weights per head that
 * were pure bias-shifters stop being.
 *
 * `METRICS_VERSION` does not move: no rule changed and no metric means
 * anything different. What changed is what `evaluate` sees.
 *
 * v24: **`scoreMove` fills the head's aimable slots, which were `evaluate`-only
 * — so the trainer's whole instrument was switched off at the call site that
 * consumes it.** `rules/trainer.ts` teaches an army to condition on an
 * instruction it sees through `focusNear`/`focusSpread`, and a harvesting crew
 * (`ai/crew.ts`) asks that trained vector to rank *moves*. Measured over five
 * crew boards before the fix: the head added a flat **42.8 centipawns to every
 * candidate** and changed the chosen step on **none** of them — a constant
 * cancels, so the network was contributing exactly nothing while looking live.
 *
 * `focusNear` is filled from the **mover's** destination rather than from the
 * army's centroid, and that is what makes it discriminate: a centroid over
 * twenty pieces shifts by a twentieth of a square when a pawn steps, which
 * after the squash is nothing, while the mover's own distance is precisely what
 * differs between two candidates. `focusSpread` stays the army-level reading it
 * is in `evaluate`, so one slot means one thing at both call sites — it
 * conditions rather than discriminates, and it is memoised on `NearCache`
 * because it is a fact about the board on the hottest function the planner has.
 *
 * Every champion is re-measured: on any board carrying objectives — a level
 * with them, and now any crew — quiet moves score differently.
 */
// 26: belt hygiene. `wantedRotations` will now re-aim a part whose successor
// feeds it back — an opposed pair was immune to rotation *and* to removal —
// and it takes dead-ended lanes before loaded ones; `uselessBelts` gained a
// third trigger on a run's age and will not lift what a free re-aim has just
// fixed; `bestBeltRoute` breaks a tie against ending nose-to-nose with our own
// track. The reference's own numbers moved with it (`deadTrack` 8 -> 10,
// `maxRemovals` 6 -> 12, `abandonedAfter` new), and `WEIGHTS` is the ruler
// every champion's score is taken against — so a stored score is a number from
// a different opponent whether or not the champion's own vector moved.
// 29: two of the head's inputs were arriving unreadable, and one of them was
// the score itself.
//
// `linear` had no `TRANSFORM` entry, so it took the centipawn default —
// `tanh(v / 900)` over a quantity that is the six subtotals *added up*. At the
// measured range (median 628, p90 2126, max 2982) that is 0.60 / 0.982 / 0.997:
// a p90 board and the best board ever seen differ by 0.015 of the input's
// range, so the head could rank a level game and not a won one. It has its own
// scale now (`LINEAR_SCALE`), which is the input a full-blend evaluation has to
// stand on.
//
// `forgeWanted` had none either, and it is a **flag** — ±1 through a divisor of
// nine hundred is ±0.0011, 686 times smaller than `choke`. Swept across the
// promoted field it moved the score by exactly nought centipawns on all eleven,
// which had been read as nothing having learned the signal rather than as a
// slot nothing could learn. Passed through, exactly as `mode` is.
//
// `headBlend` is clamped to 0..1 at the read site with them. It changes no
// champion's play — every stored value is negative and `blended` already
// short-circuited those to nought — but it is what makes `headShare` honest,
// and the two go in together because the fitness that measured this field was
// reading the unclamped number.
// 30: **the head's score output is linear, and every stored head is dropped
// for it.** The other two outputs are bounded quantities — a belt multiplier
// and a share of a depth ration — and `tanh` is right for both. A score is not:
// it has to keep two *won* boards apart as reliably as two level ones, and a
// squash spends nearly all its slope near nought. Measured at `headRange` 3000,
// which is what a full-blend evaluation has to cover: 628cp needs `z` 0.21
// where the node moves 28.7cp per hundredth of `z`, and 2982cp needs `z` 2.90
// where it moves **0.36** — an eightyfold collapse in resolution over exactly
// the boards the ramp is trying to rank. The bound comes back as a clamp, so
// `head` and `headRange` mean what they meant.
//
// **The stored heads do not carry forward, and that is the point rather than a
// cost.** A vector tuned to drive `z` to 2.9 to mean "won" now asks for 8700
// centipawns and is held at the rail; every parameter in it was selected
// against a curve that no longer exists. That is `resizeParams`' own argument
// about a shrink — "every survivor now reads a different function than the one
// it was tuned against" — arriving through a changed activation rather than a
// changed length, and it gets the same answer: dropped, said out loud, and
// retrained. The weights around it are hours of search and are kept.
// 31: **the on-ramp was half the height it claimed.** `withEcho` sized its gain
// as `cbrt(share · INPUT_SCALE / OUTPUT_SCALE)`, which is right for exactly one
// arrangement — `linear` arriving on `INPUT_SCALE`, and the score node squashing
// on the way out. v29 gave `linear` its own scale and v30 made that node linear;
// the formula tracked neither. Measured: a fresh head restated **4.5-9.2%** of
// the linear sum against the 15% `ECHO_SHARE` promises. Nothing failed — the
// ramp was simply steeper than anybody had agreed to, which only shows up when
// somebody tries to walk it.
//
// Solved by bisection now rather than derived, so the property is measured and
// the next change to a transform cannot quietly halve it again. And the share
// **tracks the rung**: `blended` is `linear * (1 - blend) + head`, so a head
// restating exactly `blend` opens a rung at parity with the model it is
// replacing instead of below it. Exact at the median board and monotone
// throughout; the tails still compress, which is the input squash and is
// structural.
//
// `withWeights` re-flashes a dead head **after** the clamps with them, so a
// replacement is seeded for the rung and the authority it will actually be
// played at — it used to run mid-loop, before either was resolved. That is what
// makes this a planner bump rather than a tuner one: for a champion whose head
// arrives flat, it changes what `evaluate` answers.
// 32: **five champions had a starved output row and nothing could reach it.**
// Belt and depth rows at a thousandth of their layer's gain while the score row
// was healthy — starved rather than saturated, because no goal scores either
// answer, so they are unrewarded parameters and selection walks them to nothing
// one generation at a time. Both existing rescues were shut: `isFlatHead` wants
// all three answers dead and the score worked, and the twin offered
// `renormalizeHead`, which scales a whole layer and therefore preserves a row's
// *relative* deadness exactly — measured, 0.003 of depth swing became 0.021
// against a threshold of 0.25, on all five.
//
// `repairHead` escalates instead — the row, then the layer, then a fresh seed
// with its rows woken — and stops at the first that works, so a head already
// reading the board is returned untouched. The migration carries it onto the
// tuner's volume, which is the only channel that reaches it: single-attach,
// so nothing outside that container can write a champion file.
//
// Verified against the live field before shipping: six byte-identical, five
// repaired, none worse, 11/11 awake on depth after.
// **33 — §27, the blow.** `scoreMove` prices a capture that did not finish
// its target as the share of health it took rather than as the piece, and the
// reply search discounts a threat it can only bash by the same share from the
// other end. Both are new terms in the two functions this number guards, and
// both change what the opponent believes a fight is worth — a champion
// searched at 32 was selected against an evaluation where a pawn taking a
// queen was a capture.
// **35 — PLAN-UI §28.4, forge co-funding.** Every foundry touching the square
// a piece lands on splits the bill, so "can this side forge" stopped being a
// question about one hopper: `readinessValue` asks `fundableSquares` instead
// of `usableFor >= mintCost`, and a side with two half-full rooks side by side
// now reads as ready where it used to read as broke. That is inside
// `evaluate`, so a champion searched at 34 was selected against an evaluation
// that priced the same board differently.
// 36: the material bundle split, so `readinessValue` prices recipes off the
// board's `bills` dial rather than off the ladder — a different bill.
// 37: belts stopped carrying pieces (§31 R4), so `scoreMove` prices the move's
// own square rather than `previewCarry`'s, and the lane planner and the brief
// dropped their ride rules (`rideIsBad`, `shovesUsBack`, `laneIsDelivering`).
// 38: the army styles went (§31 R2) — `evaluate` lost the treasury and the
// waste term, `scoreMove` the walk to a corpse, the turn its raise, upgrade
// and slither candidates.
// 39: one play profile (§31 R5) — every board resolves to the Robotic
// Monarchies', where the default and a run's enemies resolved to two
// retired styles.
// 40: the turn (§31 T) — a plan ends with End Turn, a forge-only turn is a
// candidate under Factory Flow, and the rollout plays whole turns (move, then
// End Turn) where only the mover's factory ticks.
// 41: combat numbers (§31 K) — `scoreMove`'s blow terms read the new tables
// (flat 5, HP K5 Q2 B2 N3 R3 P2), a ring out is priced as the capture it is,
// and base armor/regeneration change what a ranged threat is worth.
// 42: materials and fuel (§31 M) — `mintCeiling` and `unlockValue` read no
// oven while no coal is in reach, `unitWorth` prices coal, the queen is an
// oven, and the planner forges from `PLANNER_MINTS` against the new bills.
// 43: structures stand on cells (§31 S1) — `scoreMove` prices every building
// a move plows, crushes or breaks (`move.broken`), and a blow at a building
// loses what the plow spent.
// 44: belts are structures (§31 S2) — `viewTrack` has no drawn track to fund,
// lanes are priced as paid at once, and `move.broken` now carries belt parts.
// 45: the late verbs (§32 V1) — the plan builds, upgrades, fuses, places
// tokens, settles trophies and hands gear (`ai/verbs.ts`, free, at the root),
// and a turret re-aim is a candidate turn.
// 46: the mode light (§32 L1) — `evaluate` and `scoreMove` scale their
// subtotals by `chessModeIndustry`/`factoryModeMaterial` when a mode bit is
// set, and the head grows a 25th input, `playMode` (24x8x5x3 -> 25x8x5x3).
export const PLANNER_VERSION = 46;

/** How the opponent prices pieces, positions and industry. Plain data. */
export interface Weights {
  /**
   * Centipawns per piece type, **indexed by `PieceType` directly**.
   *
   * So index 0 is unused and there are seven entries for six pieces. That is
   * deliberate and it is the safe shape: `PAWN` is 1 and `KING` is 6, so a
   * six-entry array packed from zero puts a pawn's value on the knight and
   * runs off the end at the king — which is `undefined`, which is NaN, which
   * loses every comparison it touches, so the planner silently falls back to
   * the first legal move and stops playing. It got exactly that far here
   * before a probe caught it, and the equality test did *not*: both runs were
   * broken identically and agreed.
   */
  piece: number[];
  /**
   * What a rank is worth **to me**, by whose it is. Multipliers on `piece`.
   *
   * **One table said all three things, and three profiles claimed otherwise.**
   * `evaluate` priced every piece at `sign * w.piece[type]`: my queen, my
   * ally's queen and an enemy's queen were one number with a sign on it. That
   * cannot express what several armies are *for* —
   *
   * - `classic` says "nothing it loses can ever be replaced", and it is the
   *   army with no factory. A rook it drops is gone; a factory army mints
   *   another. Symmetric weights price both rooks identically, so the goal
   *   could reward not losing pieces (`lostPieceValuePerTurn`) while the
   *   evaluation had no knob to do it with.
   * - A capture-first goal "prices your pieces above your metal", which is
   *   a claim about *enemy* pieces specifically.
   * - An **ally** is not yours. You do not move it, you cannot
   *   spend it, and it can be mated out from under you — so counting its queen
   *   as exactly your own is the one of the three that is plainly wrong rather
   *   than merely coarse.
   *
   * Split into multipliers over the shared rank table rather than three
   * tables, because what a rank is worth is a fact about chess and this is an
   * army's attitude to it — two different kinds of number, and eighteen more
   * searchable coefficients for one idea.
   *
   * **All three default to 1, so this is arithmetically identical to what it
   * replaced**, to the last centipawn: every champion on disk scores what it
   * always scored and v19 is a re-measurement rather than a retrain. Not
   * redundant with scaling `piece` itself — that moves all three together, and
   * what these say is how they differ.
   */
  ownPiece: number;
  allyPiece: number;
  enemyPiece: number;

  // --- what a move is worth -------------------------------------------------

  /** Multiplier on a victim's scrap-and-cargo spoils. */
  spoils: number;
  /**
   * What capturing an enemy structure is worth, in centipawns (§26 F5).
   *
   * Its own term because a structure is not in `w.piece` — the capture's move
   * record carries no piece type — and the reply search reads it too, so a
   * warehouse left hanging is a loss the army can flinch from. The wrecked
   * stock is priced separately through `spoils`, exactly as a piece's cargo is.
   */
  structureValue: number;
  // --- the flow map, as an army trait --------------------------------------
  //
  // **`RULES.demand` is the default; these are this army's own** (`setDemand`,
  // `Position.demand`, `demandOf`). The demand field decides where every unit
  // of material goes, and it was one table for the whole game — so "where does
  // this army send its ore" was the one industrial question no profile could
  // have an opinion about.
  //
  // Deliberately an invitation to emergent strategy: an army that would rather
  // forge raw than cook, or one that funds its track ahead of its ovens, is a
  // coherent opponent and nothing in the field could express it. A tier is a
  // *preference measured in squares*, so the gap between two of them is how
  // far one is worth walking past the other.
  //
  // Not searchable, on purpose: `store`, which is a strict last resort rather
  // than a preference and stays a rule (PLAN §14). And `onwardCap` is clamped
  // below `foundry` at the read site whatever a search asks for, because that
  // bound is what keeps the term a comparison between ovens.
  demandRefiner: number;
  demandBlueprint: number;
  demandHungryFoundry: number;
  demandFoundry: number;
  demandOnward: number;
  demandOnwardCap: number;
  demandStranded: number;

  /**
   * Above zero, this army does not forge at all.
   *
   * **A gate, not a price**, and the only field here that is one. Every other
   * weight says how much something is worth; this says an act is off the
   * table, which is why `forgeCandidates` returns early on it rather than
   * scoring a mint at nothing — a zero `forgeAppetite` still lets a mint that
   * blocks a check through, and still lets the search wander back up.
   *
   * It exists for the **ravager** (`ai/profiles.ts`): an army that fights and
   * never builds, on a board where it could.
   *
   * **Frozen in the tuner** (`FROZEN` in `tune.mjs`), because it is an
   * identity rather than a quantity. A search allowed to jitter it would spend
   * half its generations deciding whether this profile is the thing it is.
   */
  noForge: number;

  /**
   * How much this army likes *moving* each kind of piece, by `PieceType`.
   *
   * Indexed directly, so index 0 is unused and there are seven entries for six
   * pieces — the same shape and the same reason as `piece`.
   *
   * **What it is for is the king.** A factory king mines at three times a
   * pawn's rate, refines, forges and lifts whatever it lands on, so walking it
   * to a graveyard is a real opening — and nothing in `scoreMove` could
   * express a preference for *whose* turn it is to walk. More generally an
   * army may simply like some pieces on the move more than others, and that is
   * a per-style opinion rather than a fact about chess.
   *
   * **Zero is the neutral point**, so the shipped vector adds exactly nothing
   * to any move and only a tuned one has an opinion. A uniform *non-zero*
   * default would be a flat bonus on every move and therefore a
   * move-versus-forge knob — which is `forgeAppetite`'s job, and two weights
   * arguing one question is how neither of them ends up meaning anything.
   *
   * Zero is searchable here, unlike a multiplicative-only field: `jitter` has
   * an explicit branch for it, because several defaults are zero on purpose.
   */
  movePiece: number[];

  /**
   * Centipawns per square closed on the nearest objective, on a quiet move.
   *
   * `aggression`'s shape, pointed at ground rather than at an army — and the
   * only term in the planner that can be aimed from *outside* the position.
   * Zero on every board that places none, so it is inert unless somebody has
   * something in mind.
   */
  objectivePull: number;
  /**
   * How spread out this army likes to be, in squares of mean distance from
   * its own centre.
   *
   * Positive disperses and negative concentrates, and neither is obviously
   * right: a spread army covers more ground, holds more territory and mines
   * more of the floor, and a concentrated one defends itself. Which an army *should* want is exactly
   * the sort of thing a search can answer and a person guesses at.
   */
  spread: number;

  /** Flat bonus for standing on an enemy belt part, destroying it. */
  crushBelt: number;
  /**
   * Flat value of stepping onto a lane of your **own**.
   *
   * `crushBelt`'s counterpart, and the sign is genuinely open — which is why
   * it is a searched weight rather than a constant. Standing on a lane
   * squashes the deck under it and takes the square out of the run's own
   * throughput while it stands there. Paid whether or not the lane is running.
   *
   * Not zero: a zero default has no ratio, so `styleWeights` could not carry a
   * champion's finding on it at all.
   */
  ownLaneStep: number;
  /** How much of the worst reply's loss is believed. Below 1: they may not. */
  replyBelief: number;
  /** Extra loss charged for cargo on a piece that can be taken. */
  cargoAtRisk: number;
  /** Centipawns per square closed on a drop-off while hauling. */
  haul: number;
  /**
   * Multiplier on the factory brief's own valuation of a haul.
   *
   * The brief already answers in centipawns — the value the load gains by
   * getting a square nearer a machine that would take it — so 1 means "believe
   * the factory exactly". It is a knob rather than a constant because the rest
   * of `scoreMove` is heuristic nudges and this one is a real quantity; how
   * loudly a true number should speak among approximations is a question only
   * measurement answers.
   */
  flowUrgency: number;
  /** Load, in centipawns of material, at which hauling weighs double. */
  loadReference: number;
  /** The most a full hold multiplies the haul pull (and divides the march). */
  loadCap: number;
  /** The biggest heap `greed` will pay a piece for standing on. */
  mineCap: number;
  /** Centipawns per square closed on the job already under way. */
  task: number;
  /** Half-weight multiplier on closing toward the enemy king. */
  kingPull: number;
  /**
   * A four-layer narrowing perceptron over the evaluation's own subtotals —
   * **32 parameters, and the only weight here whose *shape* is load-bearing**.
   *
   * Every other field is a coefficient, so the score is a weighted sum and no
   * weight can express an *interaction*: "material matters less once the
   * factory is running", "track is worth more when there is ore to put on it".
   * This is the smallest thing that can. Its inputs are the six subtotals both
   * `evaluate` and `scoreMove` bucket their terms into, and its output is
   * added back — see `perceptron.ts`, which owns the shape, the squash and the
   * resize policy.
   *
   * **Zeros by default, which is why nothing had to be re-measured.** A zeroed
   * network contributes exactly nothing, so every champion on disk scores what
   * it always scored and `PLANNER_VERSION` did not move. `jitter` already
   * kicks a zero additively, so the search can still find it.
   *
   * Grown, it resizes cleanly and behaviour-preservingly; shrunk, it must be
   * retrained from zeros — `resizeParams` is where that is argued.
   */
  perceptron: number[];
  /**
   * How loudly the head is allowed to speak, in centipawns at full deflection.
   *
   * **The relative authority of the nonlinear half against the linear half**,
   * and until now it was a constant that every army shared — so a style whose
   * decisions are mostly interactions ("material matters less once the factory
   * is running") and one whose whole claim is a straight material count leaned
   * on the network exactly as hard. That is the one thing about the head that
   * is obviously a *style*, and it was the one thing not searchable.
   *
   * Declared per army and searched like anything else. Sized against the
   * evaluation's own terms: at the default a saturated head is worth about two
   * pawns, which is enough to reorder near-equal turns and not enough to
   * out-argue a hanging rook.
   *
   * **A high value on an untrained head amplifies a hash.** `seedParams` gives
   * a slope rather than a finding, so a style that leans hard on the network
   * before its champion has searched one is louder about nothing — which is
   * why the industrial styles get the reach and the ones with a linear story
   * do not.
   */
  head: number;
  /**
   * How much of the evaluation comes out of the head rather than the linear sum.
   *
   * **0 is the shipped arithmetic and 1 is the whole score.** See `blended` in
   * `opponent.ts`: at 0 this is `linear + forward(…)` to the last centipawn, so
   * every champion on disk is unmoved and no version number had to turn; at 1
   * the linear sum is gone and what the evaluation *is* is the network.
   *
   * Searchable, and a dial rather than a switch, because the move is worth
   * making in steps: the head has to learn to reproduce what the linear sum was
   * saying before it can improve on it, and `withEcho` already seeds it to echo
   * `linear` for exactly that reason.
   */
  headBlend: number;
  /**
   * The head's authority once it *is* the evaluation.
   *
   * `head` is what it may add as a correction (200 by default). This is what it
   * may say on its own, and it has to cover the range the linear sum covers or
   * turning the blend up simply flattens every board toward zero — measured
   * over a played game, `|linear|` runs to a median of 628, a p90 of 2126 and a
   * max of 2982, against a correction capped at 200.
   *
   * Read only when `headBlend` is above zero, so it costs nothing until the
   * dial is turned.
   */
  headRange: number;
  /**
   * How hard a **pawn** is pulled toward somewhere it would promote.
   *
   * Paid as the *share of the remaining walk* a step completes — a step from
   * two squares out is worth half of this, from twelve squares out a twelfth —
   * so the number is what closing the last square is worth. See `scoreMove`
   * for why it is not a flat rate per square.
   *
   * Its own knob rather than a share of `aggression` or `greed`, because it is
   * neither. Marching at the wall is not closing on the enemy — on a padded
   * board it is walking away from most of them — and it is not factory work,
   * though it argues directly with it: the piece being asked to make the walk
   * is the one piece that also digs.
   *
   * Measured against the **wall** an army marches at and not against a run's
   * promotion squares — see `nearestPromotion`, where the difference is a
   * measurement rather than a preference. So it is inert on the classic board
   * and on a run's ragged ground, and live on exactly the big square boards
   * the tuner now plays half its games on.
   *
   * **Paid to a loaded foundry as well as to a pawn**, and that is the half
   * that does the work. A pawn minted onto the wall *arrives as a queen*
   * (`mintPiece`), so a rook standing beside it turns the cheapest recipe in
   * the game into the dearest piece in it — which is a walk a rook can make
   * and a pawn, ten squares out at one square a turn, cannot. Measured on the
   * sandbox board: **3.0 queens a game at zero, 9.0 at this value**.
   */
  promotionChase: number;

  // --- what a board is worth ------------------------------------------------

  /** Per unit of scrap under a working piece, capped by `miningCap`. */
  mining: number;
  /** The cap on that. */
  miningCap: number;
  /** Per unit of contested scrap, to whoever is nearer. */
  looseScrap: number;
  /** Per belt part owned. */
  beltHeld: number;
  /**
   * Charged back per belt part of ours that is earning nothing (`ai/lanes.ts`).
   *
   * **`beltHeld` is paid on every part, whether or not it does anything**, so
   * the board's opinion of a lane laid into open ground was `+beltHeld` a part
   * — the opponent was paid to build track to nowhere and then paid again for
   * keeping it. `uselessBelts` cleaned some of it up afterwards, which is a
   * different question: that decides what to *lift*, this decides whether
   * laying it was worth anything in the first place.
   *
   * Dead means either end of it: nothing feeds the part, or following it
   * forward arrives nowhere. Deliberately **larger than `beltHeld`**, so a part
   * doing nothing is a small liability (net −2) rather than a small asset —
   * and deliberately only just larger, because a lane is dead for every turn of
   * the several it takes to build one and a heavy penalty would refuse the
   * second part of every run anybody ever laid. A weight, never a gate.
   */
  deadTrack: number;
  /**
   * What a part being **paid for this tick** is worth (`FactoryView.funding`).
   *
   * Track costs iron now, so a run is a promise and the summary could not see
   * one being kept: `beltHeld` pays for track that exists and `deadTrack`
   * charges for track that earns nothing, and a blueprint is neither.
   * Measured headless with no term at all: **24 parts drawn against 5 built**.
   *
   * The rate rather than the *stock*, and that is the half worth scoring. A
   * side can be rich and building nothing — its ore nowhere near the run it
   * drew — or nearly broke and building steadily, and the two are opposite
   * situations. This one is a fact about the board (a supplier is standing
   * there); whether the whole outstanding run can ever be paid for is a
   * prediction, and it drives `wantedLanes`' gate instead — see
   * `factoryValue`, which measured what happens when it is scored.
   */
  beltFundRate: number;
  /**
   * What a unit parked on dead track is worth as "in flight": a fraction.
   *
   * Not zero. Material at the head of an unfinished lane is genuinely closer to
   * a forge than material still in the ground, and a hard zero would make
   * *completing* a lane the only way to score a haul that is already half done.
   * But it is not moving, so it must not be priced as though it were — see
   * `inFlightValue`, which used to count it at full value discounted only by
   * straight-line distance, and so paid the opponent for stranding its own
   * output near a foundry it could not reach.
   */
  strandedFlow: number;
  /**
   * Value of a unit at each rung of the **metal** line — five long from the
   * retired arm B (§31 R3); the last two rungs are never reached. The per-line copies of `RULES.materialValue`:
   * the reducer routes by that table, and these are what the *evaluation*
   * thinks each rung is worth, which the tuner may search.
   */
  grade: number[];
  /** Value of a unit at each rung of the stone line (cobble/stone/marble). */
  gradeStone: number[];
  /** Value of a special (electrum, diamond) — the deposits-only pair. */
  gradeSpecial: number[];
  /** How fast a unit's value falls off with distance to a machine: 1/(1+k·d). */
  reachFalloff: number;
  /** Stands in for "no machine anywhere". */
  farEnough: number;
  /** The most a complete production chain is worth. */
  capacity: number;
  /**
   * What an oven with a forge **downstream of it** is worth, per oven.
   *
   * The layout half of the assembly line. `capacity` prices having enough
   * machines and `throughput` prices the narrowest stage; neither asks whether
   * the stages are in the right *order*, so a side could score a full plant
   * while its ovens cooked into a corner and the forge sat upstream of them
   * waiting for metal that had to come back past the mine to reach it.
   *
   * Read off the reducer's own routing field rather than a second opinion about
   * what "downstream" means — see `orderValue`. Small: the ordering is a real
   * advantage and a smaller one than owning the machine in the first place.
   */
  order: number;
  /** Weight on material moving toward a forge — a second reading, so a fraction. */
  inFlight: number;
  /**
   * Weight on material **banked in your pieces' hands** — the spendable half.
   *
   * Split out of `material` so the two can be priced apart. They are not the
   * same thing: metal on a lane is on its way somewhere, and metal in a
   * foundry's hopper is a decision waiting to be made. Measured, a factory
   * ended games holding ~289 points of material against ~4 spent — so the
   * question "is a full warehouse worth what it costs to fill" needs a knob of
   * its own, and it did not have one.
   *
   * Still discounted by distance to a machine, exactly as it was: the reach
   * falloff is what makes a loaded piece walk home, and removing it here would
   * make a queen's hoard on the far edge of the map worth as much as a bar in
   * the foundry's own hands.
   */
  banked: number;
  /**
   * What being **able to forge, right now** is worth, as a share of the surplus
   * actually doing it would realise.
   *
   * Strictly below one, and that is the whole design. A term that paid for
   * readiness at face value would pay a side to *stay* ready — hold the metal,
   * keep the bonus, never spend it — which is the hoarding failure
   * `mintAnticipation` already had once and was corrected for. Crediting a
   * fraction leaves the rest to be collected by putting the piece on the board,
   * so being ready is worth having and never worth more than acting on.
   */
  readiness: number;
  /**
   * What each *additional* affordable recipe is worth, flat.
   *
   * A factory that can afford a knight or a bishop or a rook has a choice; one
   * that can afford four knights has a knight. Small, and flat rather than
   * proportional, because optionality is real and is not worth a piece.
   */
  readinessBreadth: number;

  // --- minting --------------------------------------------------------------

  /** How much of a mint's surplus is credited before it happens. */
  mintAnticipation: number;
  /** How much of a minted piece's value `greed` credits. */
  forgeAppetite: number;
  /**
   * How willingly a side takes one of its own pieces apart for the metal.
   *
   * Its own knob rather than `forgeAppetite`'s: a forge spends metal a side saved and this *makes* metal by spending a
   * body, so one number would set an army's willingness to melt a piece by how
   * expensive its recipes are. Low by default — the trade is a real loss on
   * the chess board and only pays when the metal unlocks something the side
   * cannot otherwise reach.
   */
  sacrificeAppetite: number;

  // --- the late verbs (§32 V1, `ai/verbs.ts`) --------------------------------
  //
  // Free verbs are priced, not judged: worth against bill, taken when positive,
  // once at the root after the turn's candidate. Each multiplies a worth the
  // rules already table, so the search moves an attitude and never a price.

  /** `build`: multiplier on `structureValue` × `structureWorth[kind]` × the kind's need. */
  buildAppetite: number;
  /** `upgrade`: share of the rank's `piece` worth a variant row adds, against bill + fuel. */
  upgradeAppetite: number;
  /** `fuse`: what the fused rank is worth over its plain rank, against the bodies eaten + bill. */
  fuseAppetite: number;
  /** `place-structure`: multiplier on `structureValue` × `structureWorth` for a token placed. */
  placeAppetite: number;
  /** `aim-turret`: orders a re-aim by enemy worth newly in the field. A move; the judge decides. */
  aimAppetite: number;
  /** `claim-trophy`: a card in hand, in centipawns — claimed when above `trophyKeep` × the score. */
  trophyCard: number;
  /** `keep-trophy`: centipawns per point of `RULES.trophyScore`. */
  trophyKeep: number;
  /** `give-equipment`: hands gear off an attacked Monarch to a safe one beside it, × `structureValue`. */
  gearSafety: number;
  /**
   * Per royal a side holds **beyond the first**, in `evaluate`.
   *
   * Prices the *board* so the rollout can see that the army which crowned
   * something is better off. Without it a coronation scores once and vanishes — `evaluate` sweeps the
   * board by piece type and a second king reads as `w.piece[KING]`, which is
   * zero.
   */
  regents: number;
  /** What blocking a check is worth. Above anything a quiet turn can bid. */
  blockValue: number;
  /**
   * What the game itself is worth, won or lost.
   *
   * Must dominate every other term put together, and by a margin: the
   * industrial half of `evaluate` routinely reaches a couple of thousand
   * centipawns and has been measured past six, so a "win bonus" of a few
   * hundred would simply be one more thing to trade away. A game is not a
   * quantity of material, it is the end of the argument about material.
   */
  winValue: number;
  /** How much better a new recipe must be before a foundry changes its mind. */
  retargetMargin: number;
  /**
   * Turns of waiting a **pawn** justifies before its recipe is abandoned.
   *
   * Every other recipe is this scaled by what it costs, so the ladder comes
   * out of the table rather than out of five more numbers — see `patienceFor`.
   * It is the escape hatch on `retargetMargin`, which is otherwise the thing
   * that keeps a foundry on a recipe it is never going to reach.
   */
  forgePatience: number;
  /** How much each piece of a type already held discounts another. */
  crowding: number;
  /** Baseline "no piece at all" bar, scaled by how stalled the army is. */
  restraintValue: number;
  /** Army size past which a further piece is crowding rather than strength. */
  crowdedArmy: number;
  /** Fraction of a stalled chain that counts toward restraint. */
  stalledRate: number;

  // --- what the army is short of -------------------------------------------

  /** Turns of digging backlog that reads as "we need another digger". */
  diggingBacklog: number;
  /** Urgency multiplier on the narrowest stage of the chain. */
  sourceUrgency: number;
  /** Share of far scrap that argues for a queen. */
  sweepShare: number;
  /** Share of stranded material that argues for a knight. */
  carryShare: number;
  /** How far a digger is assumed to walk, splitting near scrap from far. */
  sweepReach: number;
  /** Cap on scrap counted for the queen's row. */
  sweepCap: number;
  /** Centipawns a misaimed pawn adds to the knight's row. */
  reaimValue: number;
  /** Floor on how good a minted pawn's landing square can be. */
  spotFloor: number;
  /** Units at a hub before it stops attracting more. */
  hubStock: number;
  /** How fast an enemy's weight falls off with distance, for `pressure`. */
  threatFalloff: number;
  /** Aggression multiplier when nobody is leaning on this side. */
  calmAggression: number;
  /** Greed multiplier when nobody is leaning on this side. */
  calmGreed: number;

  // --- tasks ----------------------------------------------------------------

  /** Per turn a task has been under way, as a bonus multiplier. */
  momentum: number;
  /** The most momentum can compound to. */
  momentumCap: number;
  /** Centipawns of load below which a haul is not worth remembering. */
  worthHauling: number;
  /** Turns a task survives without progress. */
  patience: number;

  // --- how much thinking ----------------------------------------------------

  /**
   * Turns each candidate turn is rolled forward before it is judged.
   *
   * **It is the ration now, not the depth.** The head asks per turn for more
   * or less than this and `searchDepth` grants what the bank affords, so this
   * is what a turn is *allotted* on average rather than what it always spends.
   * With a silent head the two are the same number, which is why every
   * champion migrated into the wider shape thinks exactly as long as it did.
   *
   * Frozen in the tuner, and that is what makes the whole mechanism safe: a
   * search told to maximise anything discovers that thinking longer helps,
   * because a fitness prices no wall clock. The head may *reallocate* this
   * between turns and may never raise it.
   */
  rolloutTurns: number;
  /**
   * How far the head may swing the depth, in turns either way.
   *
   * Searchable, unlike the ration, because it is symmetric: a spread buys a
   * deep turn only by paying for a shallow one, so no value of it raises the
   * total. What it does raise is the *variance* of a turn's latency, which is
   * why `withWeights` clamps it — a saturated head at a large spread is a turn
   * that misses the supervisor's deadline and gets terminated for it.
   */
  depthSpread: number;
  /**
   * How many turns of unspent thinking may be saved up.
   *
   * The bank is what makes the allocation zero-sum rather than a request: a
   * turn that thinks shallow pays for a later one that thinks deep, and a turn
   * can only ever spend what has already been saved. Floored at nought — there
   * is no borrowing, so total thinking is bounded by the ration exactly, and
   * the head is choosing *when* to spend rather than *how much*.
   *
   * At zero the mechanism is off and every turn gets the ration.
   */
  depthBank: number;
  /**
   * How many enumerated turns are judged by rollout rather than merely ordered.
   *
   * The planner's whole budget. Each one costs about what a stance used to, so
   * this is where the 1.5s deadline is spent — and it buys accuracy with time,
   * which is why the tuner may not touch it.
   */
  candidates: number;
  /** Lanes the planner will lay in one turn. */
  maxLanes: number;
  /**
   * Parts the planner may have outstanding beyond what it can pay for, before
   * it stops drawing new ones (`wantedLanes`).
   *
   * A **gate**, not a weight, and the reason is that belt work is never
   * scored: laying is free of tempo, so `evaluate` is never
   * asked whether this run was worth drawing. Without a stop the planner drew
   * faster than its factory could pay — measured at 24 parts drawn against 5
   * built.
   *
   * Not zero: a run is drawn ahead of the ore by design, and a side that had
   * to pay in full before drawing could never start one with a miner walking
   * a lane out square by square.
   */
  beltDebt: number;
  /**
   * What a unit already in an allied army's hands is worth, under the relay
   * light.
   *
   * **The act.** Material that has crossed to the ally is the thing the relay
   * operation exists to teach, and it is priced above the approach below for
   * the reason `readiness` is bounded under forging: pay the approach at face
   * value and an army is paid to stand next to its friend holding everything.
   *
   * Inert everywhere but an operation. `relaySignal` is nought on every board
   * that does not set `SIGNAL_RELAY_ON`, which today is every board in the game
   * bar the trainer's own — so `evaluate` answers to the last centipawn what it
   * answered before this existed, and no stored champion's score moved.
   */
  relay: number;
  /**
   * What a unit of *ours* is worth for being near an ally that could take it.
   *
   * **This is the half that makes the goal climbable, and without it the whole
   * operation measures nothing.** A hand-off is a step function: until material
   * actually crosses, an army that has walked its ore nine squares closer scores
   * exactly what one that sat still scores, so generation one is flat and the
   * search has no gradient to follow. The same failure `greenDigsPerTurn`
   * already demonstrates by measuring a flat nought, and the same fix
   * `strandedFlow` uses — discount parked material rather than zeroing it.
   *
   * Discounted as `1 / (1 + gap)` rather than by a flat rate a square, so it is
   * bounded by the value carried and cannot pay an army more for a longer walk.
   */
  relayReach: number;
  /**
   * What holding the ordered *density* is worth — the packing fraction term.
   *
   * **A density, and `spread` is a distance.** That one prices how far the army
   * is from its own centre; this prices how much of the ground between its
   * pieces is filled, and the two genuinely disagree — a ring and a block can
   * share a spread and have opposite packing, and a long thin line is spread
   * out while being tightly packed inside its own narrow box. Neither derives
   * from the other.
   *
   * **Zero by default, so it is inert for the whole field.** Only a profile
   * that declares it pays anything for formation, which is what keeps this from
   * being a silent nudge on ten armies nobody asked to restructure.
   */
  packing: number;

  /**
   * Charged per unit of scrap a piece of ours is sitting on and cannot work,
   * capped by `miningCap`.
   *
   * **The state `scoreMove` already prices as a move.** That function pays a
   * non-digger to step *off* a pile — "a knight parked on a dead queen's nine
   * takes that pile out of the economy for as long as it stands there" — and
   * nothing priced the standing there, so a rollout could not see the cost of
   * a square that had been blocked for forty turns. The move term is the
   * gradient and this is the level; a fitness measuring `squatting` and an
   * evaluation with no term for it is a search that cannot climb what its own
   * games produce, which is the line `DIAGNOSTIC_METRICS` draws from the other
   * end.
   *
   * **Only the blocking half is priced.** The mirror — a *digger* standing on
   * ore — is already paid for through the ore it produced this tick, since the
   * board `evaluate` sees is post-tick and `mining`/`material` count what came
   * up. A second term there would pay twice for one act, which is the argument
   * that keeps `greenForgesPerTurn` unscored.
   *
   * **Gated on the side having a harvester at all**, exactly as the move term
   * is: an army with no diggers has no economy for the pile to be out of, and
   * `classic` and `ravager` own no piece that can work one — so an ungated
   * charge would bill them for every capture, since a capture leaves scrap
   * under the capturer.
   *
   * Nought by default, so every existing champion plays exactly as it did.
   */
  squatting: number;
  /**
   * The density it is aiming at, as a fraction, 0..1.
   *
   * **Searchable, and deliberately not 1.** "As tight as possible" is the one
   * setting that needs no target at all, and it is very likely the wrong
   * answer: a packed army is a queen's sweep away from losing several pieces at
   * once, cannot surround anything, and blocks its own lines. What the right
   * looseness *is* is a question with a number for an answer, so it is a knob
   * the search can move rather than a constant somebody guessed once.
   *
   * The default is a guess and wants the first measured pass, exactly as
   * `handed` and `greenDigsPerTurn` do.
   */
  packTarget: number;
  /**
   * Centipawns for a move that takes the army *toward* `packTarget`.
   *
   * **The chase half of the pair.** `packing` prices the shape a board is
   * already in and `evaluate` only sees that several turns into a rollout; this
   * is the per-move gradient that gets there, which is the same split
   * `scoreMove` makes elsewhere — where a fact has to be predicted one
   * move ahead it belongs in the move scorer, not the board score.
   *
   * Scored as the *improvement*, never the level, or one arrangement is paid
   * for twice and the army is rewarded for standing still in a good shape. The
   * shape `objectivePull` and the task term already use.
   *
   * Zero for the field, and it buys the whole field one comparison: this is the
   * hottest function the planner has and the term costs two board sweeps a
   * candidate, so nobody who has not asked for a formation builds them.
   */
  packingChase: number;
  /**
   * Chess mode's multiplier on the industrial subtotals (§32 L1).
   *
   * On a turn `SIGNAL_MODE_CHESS` is set, `ore`, `factory`, `mint` and
   * `network` are scaled by this in `evaluate` and `scoreMove`, so material,
   * king safety and the mate decide. **No mode bit, no scaling** — every board
   * outside the mode light evaluates exactly as before. Clamped at nought: a
   * negative would pay the army to wreck its own plant.
   */
  chessModeIndustry: number;
  /**
   * Factory mode's multiplier on the material subtotal (§32 L1).
   *
   * The mirror: on a `SIGNAL_MODE_FACTORY` turn material is scaled by this, so
   * throughput decides and a piece is worth what it is to the plant. The win
   * and the loss are untouched (`winValue` returns before any subtotal), so a
   * factory turn still takes a mate and still dodges one. Clamped at nought.
   */
  factoryModeMaterial: number;
  /**
   * Turns a dead-ended run may sit unextended before it counts as abandoned.
   *
   * The threshold on `ageBelts`, and the only thing that lets `uselessBelts`
   * touch a run something still feeds. Both wider readings of "dead" were
   * measured harmful precisely because they could not tell a half-built lane
   * from an abandoned one; this can, and the number is how much patience it
   * shows before deciding.
   *
   * **Generous on purpose.** Track is drawn as blueprints and funded a unit of
   * iron at a time, so a six-part spur is legitimately several turns from
   * carrying anything, and a lane whose supplier walked off to fight may come
   * back. The cost of waiting is a little dead track; the cost of impatience is
   * the campaign regression this mechanism exists to avoid.
   */
  abandonedAfter: number;
  /** Parts it will take up in one turn. */
  maxRemovals: number;
  /** Parts it will re-point in one turn. */
  maxRotations: number;
  /** Squares a routing walk will visit before giving up. */
  chaseLimit: number;
  /** How near something must come before a sleeping camp wakes. */
  patrolRadius: number;
}

/**
 * The numbers as they stand — every one of them what the constant it replaced
 * was set to, so extracting this changed no game.
 *
 * Where a value has a story, the story stayed with the code that reads it:
 * this is a table of numbers, and a table of numbers with thirty paragraphs in
 * it is a table nobody can see the shape of. `grep` the field name to find the
 * reasoning.
 */
export const WEIGHTS: Weights = {
  // Indexed by PieceType, which starts at 1 — index 0 is a hole on purpose.
  //          -    pawn knight bishop rook queen king
  piece: [0, 100, 300, 300, 500, 900, 0],
  ownPiece: 1,
  allyPiece: 1,
  enemyPiece: 1,

  spoils: 8,
  // The store's build cost at the rung it is paid in: `RULES.storeCost` (6)
  // x `gradeStone[1]` (16). A default, not a claim — the search owns it.
  structureValue: 96,
  // The rules' own flow map, so a side that has never been tuned routes
  // exactly as the game always did. `DEMAND_DEFAULT` rather than eight
  // literals, or the defaults could drift apart from the rule they mirror.
  demandRefiner: DEMAND_DEFAULT[DEMAND.refiner],
  demandBlueprint: DEMAND_DEFAULT[DEMAND.blueprint],
  demandHungryFoundry: DEMAND_DEFAULT[DEMAND.hungryFoundry],
  demandFoundry: DEMAND_DEFAULT[DEMAND.foundry],
  demandOnward: DEMAND_DEFAULT[DEMAND.onward],
  demandOnwardCap: DEMAND_DEFAULT[DEMAND.onwardCap],
  demandStranded: DEMAND_DEFAULT[DEMAND.stranded],
  // Seven entries, index 0 unused. All at the neutral point, so this ships
  // adding nothing to any move.
  movePiece: [0, 0, 0, 0, 0, 0, 0],
  objectivePull: 6,
  // Zero is no opinion, and this one genuinely has none until it is searched:
  // there is no prior for whether an army wants to be spread or bunched.
  spread: 0,
  noForge: 0,
  crushBelt: 30,
  ownLaneStep: 2,
  replyBelief: 0.85,
  cargoAtRisk: 8,
  haul: 22,
  flowUrgency: 1,
  loadReference: 100,
  loadCap: 4,
  mineCap: 4,
  task: 34,
  kingPull: 0.5,
  /**
   * **A live head by default** (`defaultParams`).
   *
   * This was zeros, and zeros are the degenerate case exactly: every node sums
   * to nothing, `tanh(0)` is 0 at each layer, the output is a flat zero for
   * every board, and a search handed a constant has nothing to select on.
   * Measured across a promoted field, four of eight heads were inert and one
   * was still thirty-two exact zeros after a full run.
   *
   * Fan-in scaled with zero biases, so a fresh head answers different boards
   * differently — measured std 17 centipawns, mean -0.6, against a 200 cap.
   * Small enough not to decide anything on its own, live enough that every
   * parameter has a slope from the first generation.
   *
   * **`defaultParams` and not `seedParams`, because this one may not move when
   * the shape does.** The seed is a hash of each weight's position, so a
   * reshape re-rolls it wholesale — and this vector is the ruler every champion
   * is measured against, the baseline every `styleWeights` ratio is expressed
   * against, and what a profile with no champion actually plays. Frozen at
   * `SEED_SHAPE` and grown through `resizeParams`, it migrates on exactly the
   * terms a champion does. See `SEED_SHAPE` for what this cost before it did.
   */
  perceptron: defaultParams(),
  head: OUTPUT_SCALE,
  // Zero: the evaluation is the linear sum plus a bounded correction, exactly
  // as it has always been. Turning this up is the deliberate act.
  headBlend: 0,
  // Sized on the measurement above rather than picked: a shade over the widest
  // `|linear|` seen in a played game, so a head at full blend can still say
  // what the sum it replaces was able to say.
  headRange: 3000,
  // **Sized against the smallest decision it must not swamp**, which is the
  // forge — the same argument `deadTrack` is sized by. `forgeAppetite` prices
  // a knight at 0.05 x 300 = 15 at greed 1. Paid by share of the walk, this is
  // what the *last* square is worth: at 24 a pawn one step from the wall
  // outbids a mint and a pawn twelve away is worth 2, which is the shape the
  // term is for. Flat at 12 a square was measured and is what this replaced —
  // it took the forge off `minting.test.ts`'s plan altogether. A starting
  // point for the search, not a claim.
  promotionChase: 24,

  mining: 8,
  miningCap: 6,
  looseScrap: 3,
  beltHeld: 6,
  // Net −4 on a part that earns nothing, against +6 on one that does. It was 8
  // — net −2 — and still light on purpose, because every lane is dead while it
  // is being built and this term cannot tell that from abandoned; `laneIsDead`
  // waits for cargo to park before it charges at all. Raised rather than
  // doubled again because the thing it is sized against has not moved:
  // `beltHeld` is +6 and a charge past it would make laying track a net loss
  // and stop the opponent building lanes, which is the failure the light value
  // was chosen to avoid. Searchable, and the search now has a reason to move
  // it — before this the only way to answer a dead lane was to lift it.
  deadTrack: 10,
  // **Sized against the smallest decision it must not swamp**, which is the
  // forge: `forgeAppetite` prices a whole mint in single figures, so a track
  // term in double ones decides whether to build pieces at all. A starting
  // value — the search has never seen an economy where track costs anything.
  beltFundRate: 3,
  // A quarter. Parked material is nearer a forge than unmined scrap and further
  // than material actually moving toward one.
  strandedFlow: 0.25,
  // **Bounded by what the metal can be turned into.**
  //
  // Material has exactly one sink — a recipe — so a unit of grade g cannot be
  // worth more than the cheapest piece at that grade divided by how many units
  // it takes. It was `[12, 26, 55, 110]`, which broke that at the top: ten Gold
  // for a rook scored 550 against a rook's 500, and eighteen Diamond for a
  // queen scored **1980** against her 900. So the evaluation's own numbers said
  // that holding the metal beat building the piece, and the opponent believed
  // them — measured, a factory ended games with ~289 points banked and ~4 spent.
  // Every recipe now has a positive surplus *after* the second readings are
  // paid — readiness and breadth are credited on top of the metal, so the
  // margin has to clear those too. `readiness.test.ts` pins it end to end,
  // through `evaluate`, which is what actually decides.
  // The metal ladder, five long — the last two rungs were arm B's electrum
  // and diamond (retired, §31 R3), never reached now; kept so the searched
  // weight vector keeps its shape. Steel came down 26 -> 24 with the F2 prices: a knight is 6
  // steel now (the discount retired), and at 26 the second readings — banked,
  // anticipation, readiness — summed past the knight itself, which is the
  // hoarding failure this table's bound exists to prevent
  // (`factory.test.ts`, "forging is a gain").
  grade: [12, 24, 36, 42, 48],
  // Stone builds structures rather than troops, so its rungs price under the
  // metal beside them; nothing charges stone until F3 flips the bills on, so
  // these are drafts with no gradient yet.
  gradeStone: [8, 16, 28],
  // The specials: dear because a queen's extra and the rocket's fuel are the
  // only sinks they will ever have.
  gradeSpecial: [42, 48],
  reachFalloff: 0.14,
  farEnough: 24,
  capacity: 200,
  order: 30,
  inFlight: 0.35,
  banked: 1,
  // A quarter of the surplus, which on the cheapest recipe that matters — a
  // knight at 5 Silver against a piece worth 300 — is about 42 points held
  // against 170 realised by forging. The gap is the incentive to spend.
  readiness: 0.25,
  readinessBreadth: 12,

  mintAnticipation: 0.5,
  forgeAppetite: 0.05,
  // **Well under the others, because the default case for this is "do not".**
  // A forge adds a body; this removes one, and the metal only pays when it
  // buys a rank the side is otherwise stuck below. Left findable rather than
  // zero: a zero could never be searched off the floor multiplicatively.
  sacrificeAppetite: 0.05,
  // The late verbs (§32 V1). Guesses sized off the bills: a Simple Wall (40)
  // pays against a rook three squares off its king, a first Armory against four
  // pieces with rows to buy, a Pegasus row (168 with fuel) at a knight's 0.6,
  // a Dreadnought at 1.2 of a queen against rook + bishop + 96. A token is
  // worthless unplaced, so placing outbids every bill but the Fountain's.
  buildAppetite: 1,
  upgradeAppetite: 0.6,
  fuseAppetite: 1.2,
  placeAppetite: 8,
  aimAppetite: 1,
  // A card the planner cannot yet play against ten points of score: claimed,
  // so a later planner that plays cards has a hand.
  trophyCard: 100,
  trophyKeep: 5,
  gearSafety: 1,
  // Per spare royal on the board, so the rollout keeps seeing a coronation
  // rather than paying once and forgetting.
  regents: 900,
  blockValue: 10_000,
  winValue: 1_000_000,
  retargetMargin: 1.2,
  // Eight turns for a pawn, so a queen gets a hundred and forty-four. Long
  // enough that an ordinary save is never interrupted, short enough that a
  // recipe nothing is feeding gets given up on inside a phase of the game.
  forgePatience: 8,
  crowding: 0.15,
  restraintValue: 300,
  crowdedArmy: 16,
  stalledRate: 0.25,

  diggingBacklog: 8,
  sourceUrgency: 1.5,
  sweepShare: 0.6,
  carryShare: 0.08,
  sweepReach: 6,
  sweepCap: 16,
  reaimValue: 45,
  spotFloor: 0.15,
  hubStock: 6,
  threatFalloff: 0.35,
  calmAggression: 0.35,
  calmGreed: 1.65,

  momentum: 0.09,
  momentumCap: 3,
  worthHauling: 24,
  patience: 6,

  rolloutTurns: 5,
  // Two turns either way, so the head's range is 3..7 around a ration of 5.
  // Sized against the A/B that measured 4, 5 and 6 over thirty games each:
  // depth 6 cost 17% more per game for no measurable change in length, so the
  // reach is deliberately the range that was measured rather than a wider one
  // nobody has priced.
  depthSpread: 2,
  // Two turns of savings. Enough that a run of quiet industrial turns pays for
  // one hard think, and not so much that a side can bank half a game and spend
  // it in one turn the supervisor then kills.
  depthBank: 2,
  candidates: 6,
  maxLanes: 4,
  // About a lane's worth of slack. A run is drawn ahead of its ore, and one
  // whole run outstanding is a plan; three are a habit.
  beltDebt: 5,
  // A unit is worth about its grade again for having crossed, and about a
  // sixth of that for being on its way. Both are guesses sized against
  // `w.grade` and want correcting from the first measured pass, exactly as
  // `greenDigsPerTurn`'s coefficient does — the note in `profile-shares.json`
  // says so about that one and it is the same admission here.
  relay: 30,
  relayReach: 5,
  // Off for everybody. A profile that wants a formation declares it.
  packing: 0,
  // Off for everybody, so this is inert until a profile opts in — and the
  // whole field is unmoved by its arrival.
  squatting: 0,
  // Twelve pieces over a six-by-six box. Loose enough that the army is not one
  // queen's sweep from losing half of itself and tight enough to still be a
  // formation rather than a scatter — and a guess, which is what the search is
  // for.
  packTarget: 0.35,
  packingChase: 0,
  // Each mode prices the other's half at a quarter: clearly its own goal, and
  // still not blind to the other. Guesses for the first measured pass.
  chessModeIndustry: 0.25,
  factoryModeMaterial: 0.25,
  // **Twelve, and it only became worth raising once the cascade landed.** This
  // was 6, and the note that used to sit on it was right: raising the cap alone
  // changed nothing, because `laneFed` calls every part of a dead run "fed" by
  // the part behind it, so a six-part spur retracted one part a turn however
  // high the number went. `condemn` walks the run's own facing and takes it
  // whole, which turns the cap into what it reads as — how much track a turn
  // may be given back — and a run is routinely longer than six.
  abandonedAfter: 10,
  maxRemovals: 12,
  maxRotations: 4,
  chaseLimit: 64,
  patrolRadius: 3
};

/**
 * A number that identifies this army, for salting a fallback head.
 *
 * **Not randomness and not identity — a way of not being a constant.**
 * `seedParams` is a fixed hash, so every army whose head arrives dead is handed
 * the same fifty-nine numbers and their evaluations stop differing in the one
 * place that is supposed to hold a style's interactions. A roll is not
 * available: this runs on the read path, where two clients deriving one board
 * from one log must agree to the last bit.
 *
 * So it is hashed off the candidate's own **scalar** weights. Arrays are
 * skipped, the perceptron among them, or a head salted by itself would change
 * the moment the search moved it and an army would play a different fallback
 * every generation. Key names go in with the values, so two armies that differ
 * only in which field holds a number do not collide.
 */
function saltOf(patch: Partial<Weights>): number {
  let hash = 2166136261;
  for (const [key, value] of Object.entries(patch)) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    for (let i = 0; i < key.length; i++) {
      hash = Math.imul(hash ^ key.charCodeAt(i), 16777619);
    }
    hash = Math.imul(hash ^ Math.round(value * 1000), 16777619);
  }
  return hash >>> 0;
}

/**
 * A candidate on top of the defaults, validated.
 *
 * Every entry point takes `Weights | undefined` and passes it through here, so
 * a partial candidate is a legal thing to hand in — a tuner searching six
 * fields should not have to restate the other fifty, and a candidate written
 * against last week's field list should not silently lose the fields it does
 * not know about.
 *
 * **Non-finite values are refused rather than clamped.** A NaN weight does not
 * make the opponent play badly, it makes every comparison it touches false —
 * so the planner silently falls back to the first legal move and the tuner
 * scores a broken run as merely poor, which is the one failure that would
 * corrupt a search rather than just losing a game.
 */
export function withWeights(patch?: Partial<Weights>): Weights {
  if (!patch) return WEIGHTS;
  const out: Weights = { ...WEIGHTS };
  const salt = saltOf(patch);
  for (const [key, value] of Object.entries(patch)) {
    if (!(key in WEIGHTS)) continue;
    const field = key as keyof Weights;
    if (Array.isArray(value)) {
      if (value.some((n) => typeof n !== "number" || !Number.isFinite(n))) {
        throw new Error(`weight "${key}" has a non-finite entry`);
      }
      // **Copied here and re-flashed after the loop**, not in it. A dead head is
      // replaced wherever one is read — see below — but the replacement has to
      // be seeded against the rung and the authority this army will play it at,
      // and neither is resolved until every field has been applied and clamped.
      (out[field] as number[]) = [...value];
      continue;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new Error(`weight "${key}" is not a finite number: ${String(value)}`);
    }
    (out[field] as number) = value;
  }
  // **The one bound applied at the read site rather than trusted to the
  // search**, and the same argument `onwardCap` makes: what keeps the depth
  // allocation honest is that it can never raise the ration, and a spread wide
  // enough to reach past `DEPTH_FLOOR` on one side starts asking for turns the
  // bank cannot fund on the other. Clamped here so a champion off disk, a
  // hand-written profile and a mutant all obey it.
  out.depthSpread = Math.max(0, Math.min(MAX_DEPTH_SPREAD, out.depthSpread));
  out.depthBank = Math.max(0, Math.min(MAX_DEPTH_BANK, out.depthBank));
  // **A dial from 0 to 1, clamped here because below zero it is a one-way
  // trap.** `blended` short-circuits at `blend <= 0`, so a negative reads as
  // the shipped arithmetic and costs the candidate carrying it nothing — while
  // `jitter` multiplies, and multiplication preserves sign, and
  // `degenerateKeys` only rescues an exact zero. So a weight the search once
  // kicked negative can never come back, and the whole region below nought is
  // one flat fitness plateau the search is free to wander.
  //
  // It did. Measured across the promoted field: **all eleven champions carried
  // a negative `headBlend`**, -0.23 to -5.62, and every one of them was paid
  // for it — `games.mjs` subtracted `linear * (1 - blend)` from a total that
  // `blended` had never scaled, so the reported `headShare` came out as
  // `|b| / (1 + 2|b|)` and `HEAD_LEAN` handed back up to **+20% of the whole
  // fitness** for a weight that does nothing at all. Clamped, the two readings
  // agree by construction: at 0 `linear * (1 - 0)` *is* `linear`, so the
  // short-circuit becomes an optimisation rather than a second meaning.
  out.headBlend = Math.max(0, Math.min(1, out.headBlend));
  // Multipliers, so a negative flips a subtotal's sign (§32 L1).
  out.chessModeIndustry = Math.max(0, out.chessModeIndustry);
  out.factoryModeMaterial = Math.max(0, out.factoryModeMaterial);
  /**
   * **A dead head is re-flashed wherever one is read**, not only where one is
   * bred. A stored vector can be flat — all zeros, or non-zero numbers that
   * saturate to a constant — and carrying it forward is carrying a term that
   * cannot move and cannot matter.
   *
   * `reviveHead` is deterministic, so this is safe on the read path: the same
   * champion becomes the same live head on every client, which a roll here would
   * break. The salt is what keeps that determinism from handing every army that
   * arrives dead the identical vector.
   *
   * **After the clamps, so the replacement is seeded for the game it will play.**
   * It used to run inside the field loop, where `headBlend` and `headRange` may
   * not have been applied yet — so a head refreshed at a rung was seeded to
   * restate a share of the linear sum it was no longer being asked for, at an
   * authority it was not going to be played at. Both are resolved by here.
   */
  if (Array.isArray(out.perceptron)) {
    out.perceptron = reviveHead(
      out.perceptron,
      salt,
      echoFor(out.headBlend),
      headAuthority(out)
    );
  }
  return out;
}

/**
 * What the head's score answer is worth, in centipawns at full deflection.
 *
 * **One definition, because three callers need it and they must not drift.**
 * `blended` in `opponent.ts` is what plays it; `withWeights` needs it to seed a
 * replacement head at the authority that head will actually be played at; and
 * the tuner needs it to do the same for a fresh draw. Written out three times,
 * the first change to the ramp would leave two of them behind.
 *
 * `head` is what the network may add as a *correction* to a linear evaluation.
 * `headRange` is what it may say once it *is* the evaluation. The dial runs
 * between them, because a head capped at a correction's size cannot carry a
 * score that spans thousands — turn the blend up without opening the range and
 * every board collapses toward nought.
 */
export const headAuthority = (w: Weights): number =>
  w.headBlend > 0 ? w.head + w.headBlend * (w.headRange - w.head) : w.head;

/**
 * How far the head may reach, whatever a search asks for.
 *
 * Sized against the supervisor: depth 12 was measured at 292/360/538ms on
 * 16x16/24x24/40x40 boards against a 1.5s deadline, so the ceiling is well
 * inside it — but a *plan* is many rollouts and the margin is what the deadline
 * eats. Four turns of swing on a ration of five is 1..9, which is the widest
 * range that keeps the deep end inside the measured envelope.
 */
export const MAX_DEPTH_SPREAD = 4;

/**
 * The most thinking that may be saved up, in turns.
 *
 * Bounded for the reason the spread is: the bank is what a deep turn is paid
 * for out of, so an unbounded one is a side that idles for thirty turns and
 * then spends a turn the supervisor terminates.
 */
export const MAX_DEPTH_BANK = 8;

/**
 * Complain about a payload before it is used, rather than after it did nothing.
 *
 * Everything the planner is handed from outside — a tuner's candidate, a
 * champion read off disk, and (next) the per-turn industrial brief — is *data
 * crossing a boundary*, and the failures all rhyme: it is the wrong shape, it
 * is stale, or it has grown without anyone watching. None of those throw. They
 * quietly change nothing, or quietly change everything.
 *
 * Three checks, each earned:
 *
 * - **Nothing recognised.** `withWeights` skips keys it does not know, so a
 *   tuner's champion *envelope* — `{profile, score, metrics, weights}` — was
 *   accepted whole and returned the defaults. `--tuning champion.json` played
 *   the untuned opponent and reported "no difference", and the difference was
 *   believed. A payload that changes nothing is almost always a mistake.
 * - **Unknown keys.** Tolerated on purpose (a candidate written against a
 *   later field list must still run), but worth naming: a typo'd weight is
 *   indistinguishable from a weight that did nothing, and a search would spend
 *   hours on a field it is not actually moving.
 * - **Too much.** The size ceiling is the one that catches the failure nobody
 *   is looking for: a payload that is supposed to be rebuilt per turn and is
 *   instead being appended to. It stays correct and gets slower, every turn,
 *   forever — and the symptom is "the opponent got sluggish late in a long
 *   game", which reads as a planner problem and is not one.
 */
export function guardWeights(patch: unknown): string[] {
  const out: string[] = [];
  if (patch === undefined || patch === null) return out;
  if (typeof patch !== "object") return [`weights payload is a ${typeof patch}, not an object`];

  const keys = Object.keys(patch as object);
  const known = keys.filter((key) => key in WEIGHTS);
  const unknown = keys.filter((key) => !(key in WEIGHTS));

  if (known.length === 0) {
    out.push(
      `weights payload has none of the ${Object.keys(WEIGHTS).length} known fields ` +
        `(saw: ${keys.slice(0, 6).join(", ")}${keys.length > 6 ? ", …" : ""}). ` +
        `It will change nothing — did you mean its \`.weights\` property?`
    );
  } else if (unknown.length > 0) {
    out.push(`weights payload has ${unknown.length} unknown field(s), ignored: ${unknown.join(", ")}`);
  }

  out.push(...guardPayloadSize("weights", patch, { entries: MAX_PAYLOAD_ENTRIES }));
  return out;
}

/**
 * How many entries a per-turn payload may hold before it is probably a leak.
 *
 * Generous by design: the point is not to police the size, it is to notice
 * *growth*. A brief describing every piece on a large board is a few hundred
 * entries; ten thousand is not a bigger board, it is a list nobody cleared.
 */
export const MAX_PAYLOAD_ENTRIES = 4000;

/** Bytes of JSON past which a per-turn payload is worth a word. */
export const MAX_PAYLOAD_BYTES = 512 * 1024;

/**
 * Size checks for anything handed across the boundary once per turn.
 *
 * Shared by the weights and (next) the industrial brief, because "is this
 * being rebuilt or appended to" is the same question about both, and the
 * answer has the same shape: a count that should be steady and is climbing.
 *
 * Deliberately returns warnings rather than throwing. A payload that is too
 * big is still a *playable* payload, and a planner that refused to think
 * because its brief was large would turn a performance smell into a game that
 * stops.
 */
export function guardPayloadSize(
  what: string,
  payload: unknown,
  { entries = MAX_PAYLOAD_ENTRIES, bytes = MAX_PAYLOAD_BYTES } = {}
): string[] {
  const out: string[] = [];
  let count = 0;
  const seen = new Set<object>();
  const walk = (value: unknown, depth: number): void => {
    if (count > entries * 4 || depth > 12) return;
    if (Array.isArray(value)) {
      count += value.length;
      for (const entry of value) walk(entry, depth + 1);
      return;
    }
    if (value instanceof Map || value instanceof Set) {
      count += value.size;
      return;
    }
    if (value && typeof value === "object") {
      // Cycles are not this function's business, but it must not hang on one.
      if (seen.has(value as object)) return;
      seen.add(value as object);
      const own = Object.keys(value as object);
      count += own.length;
      for (const key of own) walk((value as Record<string, unknown>)[key], depth + 1);
    }
  };
  walk(payload, 0);

  if (count > entries) {
    out.push(
      `${what} payload holds ~${count} entries, past the ${entries} expected for one turn. ` +
        `If this climbs turn on turn it is being appended to rather than rebuilt.`
    );
  }
  // Only measured once the count already looks wrong: stringifying a large
  // structure every turn to find out whether it is large is the cost this is
  // meant to warn about.
  if (count > entries) {
    try {
      const size = JSON.stringify(payload)?.length ?? 0;
      if (size > bytes) out.push(`${what} payload is ~${Math.round(size / 1024)}KB of JSON`);
    } catch {
      out.push(`${what} payload could not be measured — it may hold a cycle`);
    }
  }
  return out;
}

/**
 * Carrying a tuned vector forward when the weight schema moves.
 *
 * Three kinds of change, and only one of them needs anything written here:
 *
 * - **A field added.** `withWeights` fills it from the defaults. Nothing to do.
 * - **A field removed.** `withWeights` ignores what it does not know. Nothing
 *   to do.
 * - **A field *renamed*, split, merged, or rescaled.** This is the one that
 *   loses work silently — the old name is simply not recognised, the new one
 *   takes its default, and a number somebody spent an hour of search on is
 *   quietly gone. That is what this table is for.
 *
 * Each entry moves a vector from the version below it to `to`. They apply in
 * order, so a champion three versions old walks forward through all three.
 *
 * The table is empty because no rename has happened yet. It exists now rather
 * than when the first one does, because the first one will not announce
 * itself: a renamed weight does not fail, it reverts to a default and scores
 * slightly worse, which reads as the search having got unlucky.
 */
export const MIGRATIONS: {
  to: number;
  note: string;
  apply(weights: Record<string, unknown>): void;
}[] = [
  // A worked example of the shape, for whoever writes the first real one:
  //
  // {
  //   to: 2,
  //   note: "haul split into haul (flat fallback) and flowUrgency (brief)",
  //   apply: (w) => {
  //     if (typeof w.haul === "number" && w.flowUrgency === undefined) {
  //       w.flowUrgency = w.haul / WEIGHTS.haul;
  //     }
  //   }
  // }
  //
  // And the one for **growing the perceptron**, which is the only migration
  // that has to name a shape. A stored vector carries its length, not its
  // layout, so the person widening `SHAPE` is the only one who knows what it
  // was — write it down here and the resize is exact:
  //
  // {
  //   to: 11,
  //   note: "perceptron 4x2x2x1 -> 6x3x2x1",
  //   apply: (w) => {
  //     if (!Array.isArray(w.perceptron)) return;
  //     // `resizeParams` comes from `./perceptron`; import it when you write this.
  //     const grown = resizeParams(w.perceptron as number[], [4, 2, 2, 1]);
  //     if (grown) w.perceptron = grown;
  //     // Left alone if it refuses — `migrateWeights` drops a mismatched
  //     // length below and says the network is being retrained, which is the
  //     // honest outcome for a shrink.
  //   }
  // }
  {
    to: 12,
    note: "perceptron 6x3x2x1 -> 7x3x2x1 (turn count added as a seventh input)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **The shape it was is written here because a stored vector cannot say
      // it.** Thirty-two numbers carry their length and not their layout, so
      // this is the only place that knows they were `6x3x2x1` — and with that,
      // `resizeParams` puts every one of them back in the slot it means, and
      // pads the three the new input adds. A champion keeps its finding.
      const grown = resizeParams(w.perceptron as number[], [6, 3, 2, 1]);
      if (grown) w.perceptron = grown;
      // Left alone if it refuses, which `migrateWeights` then reports as a
      // retrain rather than pretending the numbers still mean anything.
    }
  },
  {
    to: 13,
    note: "perceptron 7x3x2x1 -> 13x3x2x1 (linear score in, five slots held open)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **Both grows have to be here, and in order.** A champion written
      // before v12 arrives with 32 numbers, is grown to 35 by the entry above,
      // and then to 53 by this one — `migrateWeights` runs every migration
      // newer than the vector's own version, so the chain is what makes a
      // two-shape-old champion land in the right slots instead of being thrown
      // away as a length mismatch.
      const grown = resizeParams(w.perceptron as number[], [7, 3, 2, 1]);
      if (grown) w.perceptron = grown;
    }
  },
  {
    to: 17,
    note: "perceptron 13x3x2x1 -> 13x3x2x2 (a second output: how much track to lay)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **A grow in the output layer, which is the cheapest kind.** The final
      // layer's parameters sit last in the vector, so the second node's two
      // weights and its bias are *appended* — every stored number keeps
      // reading the slot it always read, and the three new ones are padded
      // with zero. A zero second output exponentiates to a belt multiplier of
      // exactly one, so a migrated champion lays precisely the track it laid
      // before until the search moves it.
      const grown = resizeParams(w.perceptron as number[], [13, 3, 2, 1]);
      if (grown) w.perceptron = grown;
    }
  },
  {
    to: 18,
    note: "perceptron 13x3x2x2 -> 13x3x2x3 (a third output: how hard to think)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // The same append as v17, one node further along: three more numbers at
      // the end of the vector and every stored one still reading its own slot.
      // A zero third output is a depth offset of nothing, so a migrated
      // champion rolls forward exactly `rolloutTurns` turns as it always did —
      // which is what makes this bump a re-measurement rather than a retrain.
      const grown = resizeParams(w.perceptron as number[], [13, 3, 2, 2]);
      if (grown) w.perceptron = grown;
    }
  },
  {
    to: 21,
    note: "perceptron 13x3x2x3 -> 13x8x5x3 (the waist widened, not deepened)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **A widen and deliberately not a deepen**, which is the difference
      // between this being free and costing every champion its head.
      // `resizeParams` refuses a change in layer *count* outright — layer count
      // is part of the shape — so a deeper network cannot be migrated at all
      // and every stored vector would be dropped and retrained from zeros.
      // Growing the existing layers keeps them: every stored weight lands in
      // the slot it always read and the new ones are padded with nought, so a
      // migrated champion computes exactly what it computed.
      //
      // Width was also the thing worth buying. Thirteen inputs were being
      // squeezed through a **two-wide waist**, so everything the head knew had
      // to pass through two numbers; more layers of three would have made the
      // same narrow signal travel further rather than widening the pipe. And
      // the search is mutation hill-climbing with a population already under
      // the `4 + 3 ln(D)` rule of thumb — stacked `tanh` through a narrow waist
      // with no gradient to push signal down it saturates rather than learns.
      const grown = resizeParams(w.perceptron as number[], [13, 3, 2, 3]);
      if (grown) w.perceptron = grown;
    }
  },
  {
    to: 22,
    note: "perceptron 13x8x5x3 -> 17x8x5x3 (four distance inputs: ore, the enemy, and two aimable)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **A grow in the *input* layer, which is the expensive kind to get
      // wrong.** The first layer's parameters are laid out input-major, so four
      // more inputs interleave eight new numbers into the middle of the vector
      // rather than appending to it — every weight after the first hidden
      // node's row would read the wrong slot if this were left to a length
      // check. `resizeParams` does it properly given the shape it was, and this
      // is the only place that knows the shape it was.
      //
      // **`[13, 8, 5, 3]` and not `[13, 3, 2, 3]`, which is the whole reason
      // this is a second entry rather than an edit to the one above.** A vector
      // stored before v21 walks both: the entry above puts it in the wide waist
      // and this one widens its input layer, each naming the shape it actually
      // grew from. Written as one step from `[13, 3, 2, 3]` it would claim a
      // two-wide waist for a vector that already has a five-wide one, and every
      // weight past the first layer would land in a stranger's slot — silently,
      // since the arithmetic works and only the meaning is wrong.
      const grown = resizeParams(w.perceptron as number[], [13, 8, 5, 3]);
      if (grown) w.perceptron = grown;
      // Padded with zero, and the four new inputs are read through a transform
      // that answers nought for "there is none of that on this board" — so a
      // migrated champion scores exactly what it scored until the search finds
      // something to put in them.
    }
  },
  {
    to: 25,
    note: "perceptron 17x8x5x3 -> 18x8x5x3 (the forge light, as a signed mask)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // The third entry naming its own shape, for the reason the second one
      // does: a vector stored before v21 walks all three — waist, then four
      // distance inputs, then this — and each step only lands its weights in
      // the right slots because it says which shape it is growing *from*.
      //
      // v23 and v24 moved no shape, so there is nothing between v22 and this.
      const grown = resizeParams(w.perceptron as number[], [17, 8, 5, 3]);
      if (grown) w.perceptron = grown;
      // Zero-padded, and the slot's neutral is a plain nought meaning "nobody
      // gave an order" — which is what every board outside the tuner carries.
      // So a migrated champion is unmoved until a search finds something.
    }
  },
  {
    to: 27,
    note: "perceptron 18x8x5x3 -> 24x8x5x3 (six delta slots: what a decision changed)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **The fourth entry naming its own shape**, and the chain still only
      // composes because each one says what it is growing *from*.
      //
      // What the six buy is the thing the field was measured to be missing: a
      // head whose whole contribution ranged nought to five centipawns across
      // forty candidate moves, against a linear span of seventeen to
      // sixty-six. The absolutes it reads move by tens against a range of
      // thousands, so `tanh` flattens them and the network describes the
      // *board* rather than the decision. These carry the difference.
      //
      // A migrated champion is unmoved to within the padding: the slots are
      // nought wherever a caller has no baseline, and nought is also "this
      // changed nothing", so the two readings agree.
      const grown = resizeParams(w.perceptron as number[], [18, 8, 5, 3]);
      if (grown) w.perceptron = grown;
    }
  },
  {
    to: 30,
    note: "perceptron score output went linear — the stored head is retrained rather than reinterpreted",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **The first entry that is not a reshape, and the first that throws a
      // head away on purpose.** Every one above grows a vector into a wider
      // network and is behaviour-preserving by construction: the stored
      // parameters keep reading the same inputs through the same curve and the
      // padding contributes nothing. This one changes the *curve*.
      //
      // A head selected under `tanh` was selected to put `z` where the squash
      // wanted it — the field's own numbers run to a mean |z| of 1.5 with a
      // third of boards past 2 — and linear, those same numbers ask for four
      // and five thousand centipawns and sit on the clamp. Carrying it forward
      // would hand the search a vector that looks trained and is a stranger to
      // every board it was tuned on, which is precisely what `resizeParams`
      // refuses to do for a shrink. Same answer here: drop it, say so, retrain.
      //
      // The length is right, so nothing below catches it — this is the only
      // thing that can. `withWeights` re-seeds the empty slot on the read path
      // and `island.mjs` seeds a fresh head for a search.
      delete w.perceptron;
    }
  },
  {
    to: 32,
    note: "a head with a starved output row is repaired rather than carried",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // **The one channel that reaches the tuner's volume**, and the reason
      // this is a migration rather than an operator running a script: the
      // volume is single-attach to the tuner service, so nothing outside that
      // container can write a champion file. Code that runs on the box can, and
      // a migration is the mechanism already built for "a stored vector needs
      // fixing on the way in".
      //
      // What it is fixing: five of eleven champions had belt and depth output
      // rows at a thousandth of their layer's gain while their score row was
      // healthy — starved, not saturated, because no goal scores either answer
      // and an unrewarded parameter is one selection walks to nothing. Neither
      // existing rescue could reach it. `isFlatHead` needs all three answers
      // dead and the score worked; the twin offered `renormalizeHead`, which
      // scales a whole layer and so preserves a row's *relative* deadness
      // exactly — measured, 0.003 of depth swing became 0.021 against a
      // threshold of 0.25. They had been idle for up to forty generations with
      // no path out, because the eviction gate needs a worthy candidate in the
      // pool and every head in the pool descended from the same starved one.
      //
      // **Self-limiting, which is what makes it safe to run over everybody.**
      // `repairHead` escalates from the row to the layer to a fresh seed and
      // stops at the first that works, so a head already reading the board is
      // returned untouched. Verified against the live field before shipping:
      // six byte-identical, five repaired, none made worse, 11/11 awake after.
      //
      // Salted per army off its own scalar weights, or the two whose rows are
      // beyond rescaling would wake as the same numbers — `saltOf`'s own
      // argument about a shared fallback.
      w.perceptron = repairHead(w.perceptron as number[], saltOf(w as Partial<Weights>));
    }
  },
  {
    to: 45,
    note: "eight verb weights added (§32 V1) — filled from the defaults, nothing renamed",
    // A field added needs nothing (`withWeights` fills it); the entry is the
    // record that v45 widened the vector, so a promote's notes say so.
    apply: () => {}
  },
  {
    to: 46,
    note: "perceptron 24x8x5x3 -> 25x8x5x3 (the mode light, `playMode`, appended)",
    apply: (w) => {
      if (!Array.isArray(w.perceptron)) return;
      // Appended last in `INPUTS`, so every stored weight keeps its slot and
      // the new column pads with nought; `playMode` is nought on every board
      // without a mode bit, so a migrated head is unmoved.
      const grown = resizeParams(w.perceptron as number[], [24, 8, 5, 3]);
      if (grown) w.perceptron = grown;
    }
  }
];

/**
 * Bring a stored vector up to the current planner, and say what was done.
 *
 * Returns the migrated patch and the notes, rather than applying it — the
 * caller decides whether to trust it, and a migration that happened silently
 * is a migration nobody can audit when the numbers look wrong afterwards.
 */
export function migrateWeights(
  stored: Record<string, unknown>,
  from: number
): { weights: Partial<Weights>; notes: string[]; applied: number } {
  const out: Record<string, unknown> = { ...stored };
  const notes: string[] = [];
  let applied = 0;
  for (const migration of MIGRATIONS) {
    if (migration.to <= from) continue;
    migration.apply(out);
    notes.push(`v${migration.to}: ${migration.note}`);
    applied++;
  }
  // Anything the current schema does not know is dropped here rather than at
  // `withWeights`, so it can be *named*. A vector arriving with fields nobody
  // recognises is either a migration that has not been written or a file from
  // a branch, and both are worth a word.
  const unknown = Object.keys(out).filter((key) => !(key in WEIGHTS));
  if (unknown.length > 0) {
    notes.push(`dropped ${unknown.length} unrecognised field(s): ${unknown.join(", ")}`);
  }

  // **The network's shape, which is the one field where a wrong length is not
  // a wrong number but a different function.**
  //
  // Two halves, and the asymmetry is the policy. A *grow* is handled by a
  // `MIGRATIONS` entry above, written by whoever changed `SHAPE` — they are the
  // only one who knows what the old shape was, since a stored vector carries
  // its length and not its layout — and `resizeParams` pads it behaviour-
  // preservingly, so the champion carries on scoring exactly what it scored.
  // Anything that reaches here still the wrong length is a *shrink* or a shape
  // nobody wrote a migration for, and both mean the same thing: every surviving
  // parameter now reads a different function than the one it was tuned
  // against. So it is dropped and said out loud. The weights around it
  // are hours of search and are kept; the network is not silently reinterpreted.
  const head = out.perceptron;
  if (Array.isArray(head) && head.length !== PERCEPTRON_PARAMS) {
    delete out.perceptron;
    notes.push(
      `perceptron was ${head.length} parameters, this planner has ` +
        `${PERCEPTRON_PARAMS} (${SHAPE.join("x")}) — retraining it from a fresh seed`
    );
  }

  return { weights: withWeights(out as Partial<Weights>), notes, applied };
}

/**
 * The weights, grouped by what they are *about*.
 *
 * Not decoration: these are the units a search should move together. The
 * opponent has layers that answer different questions — what a single move is
 * worth, what a whole board is worth, what to build, what the army is short
 * of, and what it is *in the middle of doing* — and tuning all of them at once
 * means every generation changes five things and the fitness cannot say which
 * one helped.
 *
 * `plan` is the one worth calling out. It is the only layer with **memory**:
 * how long a job under way stays worth finishing, how much a half-done thing
 * is credited, how long patience lasts. Everything else re-derives itself from
 * the board every turn, so those are the numbers that decide whether the
 * opponent can carry out a multi-turn intention at all — and they are exactly
 * the ones a horizon-limited search cannot discover for itself.
 */
/**
 * This vector's flow map, in `DEMAND_SLOTS` order, for the opening command.
 *
 * **One converter, because two would be two answers.** The tuner's rig sets a
 * candidate's map up before it plays and the game sets a profile's up when it
 * opens a board; a second reading of which weight is which slot would let a
 * champion be searched under one plumbing and played under another, and the
 * symptom would be an army that scores well in the box and routes differently
 * on a real board.
 *
 * `setDemand` treats a row equal to the defaults as no override at all, so a
 * side that has never been tuned leaves `Position.demand` null and costs the
 * clone, the hash and the growth path nothing.
 */
export function demandRow(w: Weights): number[] {
  const row: number[] = [];
  row[DEMAND.refiner] = w.demandRefiner;
  row[DEMAND.blueprint] = w.demandBlueprint;
  row[DEMAND.hungryFoundry] = w.demandHungryFoundry;
  row[DEMAND.foundry] = w.demandFoundry;
  row[DEMAND.onward] = w.demandOnward;
  row[DEMAND.onwardCap] = w.demandOnwardCap;
  row[DEMAND.stranded] = w.demandStranded;
  return row;
}

export const WEIGHT_GROUPS: Record<string, (keyof Weights)[]> = {
  /** What one move is worth. The per-candidate ordering heuristics. */
  move: [
    "spoils", "structureValue", "crushBelt", "ownLaneStep", "movePiece", "objectivePull", "replyBelief",
    // The chase half of the formation pair. In `move` and not in `board`
    // because `scoreMove` is what reads it — `packing` and `packTarget` are the
    // board's, and `packTarget` is deliberately shared by both, since an army
    // aiming at one density while walking toward another is two instructions.
    "packingChase",
    "cargoAtRisk", "haul", "flowUrgency", "loadReference", "loadCap",
    "mineCap", "task", "kingPull", "promotionChase"
  ],
  /**
   * The nonlinear head, alone in its group.
   *
   * Its own so that `--params head` searches the network and nothing else, and
   * `--params move` searches the linear move terms without disturbing it — the
   * two are different claims about the same board, and a run that moved both
   * could not say which one paid.
   */
  head: ["perceptron", "head", "headBlend", "headRange"],
  /**
   * Where this army sends its material — the flow map as an army trait.
   *
   * Its own group because it is the only part of this vector that is not an
   * *opinion about a board*: it is board setup the reducer routes by, so a
   * search over it is a search over how the army's economy is plumbed rather
   * than over how it judges what it sees.
   */
  flow: [
    "demandRefiner", "demandBlueprint", "demandHungryFoundry",
    "demandFoundry", "demandOnward", "demandOnwardCap", "demandStranded"
  ],
  /** What a whole board is worth. The judge. */
  board: [
    "piece", "ownPiece", "allyPiece", "enemyPiece",
    "mining", "miningCap", "looseScrap", "beltHeld", "deadTrack",
    "beltFundRate",
    "strandedFlow", "grade", "gradeStone", "gradeSpecial", "reachFalloff", "farEnough", "capacity", "order",
    "inFlight", "banked", "winValue", "spread",
    // The relay pair. In `board` rather than a group of their own because they
    // price a board the same way `banked` and `inFlight` do — where material is
    // and what it is worth for being there — and `--params board` should reach
    // them for the same reason it reaches those two.
    "relay", "relayReach",
    // Formation. `packTarget` is in the same group as the weight that spends
    // it, because searching how much an army cares about its density without
    // searching *which* density is searching half a question.
    "packing", "packTarget",
    // A charge on a board state, so it belongs beside `looseScrap` and
    // `banked` rather than in `move` — the *move* half of this idea is
    // `mineCap`, which is already in that group.
    "squatting",
    // The mode light's two multipliers (§32 L1): how a whole board is priced
    // on a chess turn and on a factory turn.
    "chessModeIndustry", "factoryModeMaterial"
  ],
  /** What to build, and whether to build at all. */
  mint: [
    "mintAnticipation", "forgeAppetite", "noForge",
    "sacrificeAppetite",
    "regents",
    "blockValue",
    "retargetMargin", "forgePatience", "crowding", "restraintValue", "crowdedArmy", "stalledRate",
    "readiness", "readinessBreadth"
  ],
  /** The late verbs (§32 V1): build, upgrade, fuse, place, aim, trophies, gear. */
  verbs: [
    "buildAppetite", "upgradeAppetite", "fuseAppetite", "placeAppetite", "aimAppetite",
    "trophyCard", "trophyKeep", "gearSafety"
  ],
  /** What the army is short of — the needs table and the posture. */
  needs: [
    "diggingBacklog", "sourceUrgency", "sweepShare", "carryShare", "sweepReach",
    "sweepCap", "reaimValue", "spotFloor", "hubStock", "threatFalloff",
    "calmAggression", "calmGreed"
  ],
  /** The only layer with memory: whether a multi-turn intention survives. */
  plan: ["momentum", "momentumCap", "worthHauling", "patience"],
  /** How a dormant camp behaves before anybody reaches it. */
  patrol: ["patrolRadius"],
  /** How much thinking. Frozen in the tuner — these buy accuracy with time. */
  budget: [
    "rolloutTurns", "depthSpread", "depthBank", "candidates", "maxLanes",
    "beltDebt", "maxRemovals", "maxRotations", "chaseLimit", "abandonedAfter"
  ]
};

/**
 * Resolve a `--params` selection into field names.
 *
 * Accepts group names and field names mixed, because both are things a person
 * reaches for: "tune the plan layer" and "tune `haul` and `flowUrgency`" are
 * the same request at two zoom levels. An unknown name is an error rather than
 * a silent no-op — a typo'd field would otherwise read as a search that found
 * nothing, which is indistinguishable from a search that found nothing.
 */
/**
 * Weights this army can never move, so a search must not spend generations on
 * them.
 *
 * **Two, since §31 R2.** The style bundles (`kingRaises`, `kingClones`,
 * `kingReplicates`, `buildsNetwork`, `playsIndustry`) went with the styles.
 * What is left is run-gated — a crown comes off a card and Sacrifice off an
 * artifact — and the tuner plays no runs, so neither has a gradient there.
 * Keyed on `kingKind` still: the door a faction (§31 R5) gates through.
 */
export function inertWeights(_kingKind: number): (keyof Weights)[] {
  return ["regents", "sacrificeAppetite"];
}

export function resolveParams(names: readonly string[]): (keyof Weights)[] {
  const out = new Set<keyof Weights>();
  for (const name of names) {
    if (name === "all") {
      for (const key of Object.keys(WEIGHTS) as (keyof Weights)[]) out.add(key);
      continue;
    }
    const group = WEIGHT_GROUPS[name];
    if (group) {
      for (const key of group) out.add(key);
      continue;
    }
    if (name in WEIGHTS) {
      out.add(name as keyof Weights);
      continue;
    }
    throw new Error(
      `unknown weight or group "${name}". Groups: ${Object.keys(WEIGHT_GROUPS).join(", ")}`
    );
  }
  return [...out];
}
